import { useState } from "react";
import { Folder, FolderOpen, Plus, Edit2, Trash2, Palette, MoreVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useFolders } from "@/hooks/folders";
import { DocumentFolderWithCount, DEFAULT_FOLDER_COLORS } from "@/types/knowledge/document-folder";
import { cn } from "@/lib/utils";

interface FolderManagementProps {
  onFolderSelect?: (folderId: string | undefined) => void;
  selectedFolderId?: string;
}

export const FolderManagement = ({ onFolderSelect, selectedFolderId }: FolderManagementProps) => {
  const { folders, isLoading, createFolder, updateFolder, deleteFolder, isCreating, isUpdating, isDeleting } =
    useFolders();

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingFolder, setEditingFolder] = useState<DocumentFolderWithCount | null>(null);
  const [deletingFolder, setDeletingFolder] = useState<DocumentFolderWithCount | null>(null);

  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderColor, setNewFolderColor] = useState(DEFAULT_FOLDER_COLORS[0]);
  const [editFolderName, setEditFolderName] = useState("");
  const [editFolderColor, setEditFolderColor] = useState("");

  const handleCreateFolder = () => {
    if (!newFolderName.trim()) return;

    createFolder(
      {
        name: newFolderName.trim(),
        color: newFolderColor,
        icon: "folder",
      },
      {
        onSuccess: () => {
          setShowCreateDialog(false);
          setNewFolderName("");
          setNewFolderColor(DEFAULT_FOLDER_COLORS[0]);
        },
      }
    );
  };

  const handleEditFolder = () => {
    if (!editingFolder || !editFolderName.trim()) return;

    updateFolder(
      {
        folderId: editingFolder.id,
        input: {
          name: editFolderName.trim(),
          color: editFolderColor,
        },
      },
      {
        onSuccess: () => {
          setShowEditDialog(false);
          setEditingFolder(null);
          setEditFolderName("");
          setEditFolderColor("");
        },
      }
    );
  };

  const handleDeleteFolder = () => {
    if (!deletingFolder) return;

    deleteFolder(deletingFolder.id, {
      onSuccess: () => {
        setShowDeleteDialog(false);
        setDeletingFolder(null);
        // If the deleted folder was selected, clear selection
        if (selectedFolderId === deletingFolder.id) {
          onFolderSelect?.(undefined);
        }
      },
    });
  };

  const openEditDialog = (folder: DocumentFolderWithCount) => {
    setEditingFolder(folder);
    setEditFolderName(folder.name);
    setEditFolderColor(folder.color || DEFAULT_FOLDER_COLORS[0]);
    setShowEditDialog(true);
  };

  const openDeleteDialog = (folder: DocumentFolderWithCount) => {
    setDeletingFolder(folder);
    setShowDeleteDialog(true);
  };

  return (
    <div className="space-y-2">
      {/* Header with Create Button */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-700">Folders</h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowCreateDialog(true)}
          className="h-8 text-genie-primary hover:text-genie-primary-hover hover:bg-genie-primary-light"
        >
          <Plus className="h-4 w-4 mr-1" />
          New Folder
        </Button>
      </div>

      {/* "All Documents" option */}
      <button
        onClick={() => onFolderSelect?.(undefined)}
        className={cn(
          "w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors",
          selectedFolderId === undefined
            ? "bg-genie-primary/10 text-genie-primary font-medium"
            : "text-gray-700 hover:bg-gray-100"
        )}
      >
        <div className="flex items-center gap-2">
          <FolderOpen className="h-4 w-4" />
          <span>All Documents</span>
        </div>
      </button>

      {/* "Uncategorized" option */}
      <button
        onClick={() => onFolderSelect?.('null')}
        className={cn(
          "w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors",
          selectedFolderId === 'null'
            ? "bg-genie-primary/10 text-genie-primary font-medium"
            : "text-gray-700 hover:bg-gray-100"
        )}
      >
        <div className="flex items-center gap-2">
          <Folder className="h-4 w-4" />
          <span>Uncategorized</span>
        </div>
      </button>

      {/* Folders List */}
      {isLoading ? (
        <div className="text-sm text-gray-500 px-3 py-2">Loading folders...</div>
      ) : folders.length === 0 ? (
        <div className="text-sm text-gray-500 px-3 py-2">No folders yet</div>
      ) : (
        <div className="space-y-1">
          {folders.map((folder) => (
            <div
              key={folder.id}
              className={cn(
                "group w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer",
                selectedFolderId === folder.id
                  ? "bg-genie-primary/10 text-genie-primary font-medium"
                  : "text-gray-700 hover:bg-gray-100"
              )}
              onClick={() => onFolderSelect?.(folder.id)}
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <Folder className="h-4 w-4 shrink-0" style={{ color: folder.color || "#3B82F6" }} />
                <span className="truncate">{folder.name}</span>
                {folder.document_count !== undefined && folder.document_count > 0 && (
                  <span className="text-xs text-gray-500">({folder.document_count})</span>
                )}
              </div>

              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => openEditDialog(folder)}>
                    <Edit2 className="h-4 w-4 mr-2" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => openDeleteDialog(folder)} className="text-red-600">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ))}
        </div>
      )}

      {/* Create Folder Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Folder</DialogTitle>
            <DialogDescription>Give your folder a name and choose a color to organize your documents.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="folder-name">Folder Name</Label>
              <Input
                id="folder-name"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="e.g., Marketing Materials"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleCreateFolder();
                  }
                }}
              />
            </div>
            <div className="space-y-2">
              <Label>Folder Color</Label>
              <div className="flex gap-2 flex-wrap">
                {DEFAULT_FOLDER_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setNewFolderColor(color)}
                    className={cn(
                      "h-8 w-8 rounded-md border-2 transition-all",
                      newFolderColor === color ? "border-gray-900 scale-110" : "border-gray-300 hover:scale-105"
                    )}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} disabled={isCreating}>
              Cancel
            </Button>
            <Button onClick={handleCreateFolder} disabled={isCreating || !newFolderName.trim()}>
              {isCreating ? "Creating..." : "Create Folder"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Folder Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Folder</DialogTitle>
            <DialogDescription>Update the folder name or color.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-folder-name">Folder Name</Label>
              <Input
                id="edit-folder-name"
                value={editFolderName}
                onChange={(e) => setEditFolderName(e.target.value)}
                placeholder="Folder name"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleEditFolder();
                  }
                }}
              />
            </div>
            <div className="space-y-2">
              <Label>Folder Color</Label>
              <div className="flex gap-2 flex-wrap">
                {DEFAULT_FOLDER_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setEditFolderColor(color)}
                    className={cn(
                      "h-8 w-8 rounded-md border-2 transition-all",
                      editFolderColor === color ? "border-gray-900 scale-110" : "border-gray-300 hover:scale-105"
                    )}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)} disabled={isUpdating}>
              Cancel
            </Button>
            <Button onClick={handleEditFolder} disabled={isUpdating || !editFolderName.trim()}>
              {isUpdating ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Folder Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Folder</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deletingFolder?.name}"? Documents in this folder will be moved to
              Uncategorized.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteFolder} disabled={isDeleting}>
              {isDeleting ? "Deleting..." : "Delete Folder"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

